import Icon from '@/asset/Icon'
import { $insertList } from '@lexical/list'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  $isQuoteNode,
} from '@lexical/rich-text'
import {
  $getSelectionStyleValueForProperty,
  $patchStyleText,
  $setBlocksType,
} from '@lexical/selection'
import { mergeRegister } from '@lexical/utils'
import {
  $createParagraphNode,
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
} from 'lexical'
import { useCallback, useEffect, useRef, useState } from 'react'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import Dropdown from 'react-bootstrap/Dropdown'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Form from 'react-bootstrap/Form'
import InputGroup from 'react-bootstrap/InputGroup'
import type { BlockType, FontSize } from './command'
import {
  BLOCK_HEADING_LEVELS,
  FONT_SIZES,
  FORMAT_BLOCK_HEADING_COMMAND,
  FORMAT_BLOCK_LIST_COMMAND,
  FORMAT_BLOCK_PARAGRAPH_COMMAND,
  FORMAT_BLOCK_QUOTE_COMMAND,
  FORMAT_COLOR_COMMAND,
  FORMAT_FONT_SIZE_COMMAND,
} from './command'

export type TextTypography = {
  color: string
  fontSize: FontSize
  blockType: BlockType
}

const DEFAULT_TEXT_TYPOGRAPY: TextTypography = {
  color: '#212529',
  fontSize: '16px',
  blockType: 'paragraph',
}

export default function TextTypographyUIPlugin() {
  const [editor] = useLexicalComposerContext()

  const [textTypography, setTextTypography] = useState<TextTypography>(DEFAULT_TEXT_TYPOGRAPY)
  const typographyRef = useRef(textTypography)

  useEffect(() => {
    typographyRef.current = textTypography
  }, [textTypography])

  const updateUI = useCallback(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode()
      const element = anchorNode.getTopLevelElementOrThrow()
      let blockType: BlockType = 'paragraph'
      if ($isHeadingNode(element)) {
        blockType = element.getTag() as BlockType
      } else if ($isQuoteNode(element)) {
        blockType = 'quote'
      } else if ($isParagraphNode(element)) {
        blockType = 'paragraph'
      }

      const color = $getSelectionStyleValueForProperty(
        selection,
        'color',
        DEFAULT_TEXT_TYPOGRAPY.color
      )
      const fontSize = $getSelectionStyleValueForProperty(
        selection,
        'font-size',
        DEFAULT_TEXT_TYPOGRAPY.fontSize
      )

      setTextTypography({
        color,
        fontSize: fontSize as FontSize,
        blockType,
      })
    }
  }, [])

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => updateUI())
      }),
      // TODO: check if it is necessary
      /*
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, _newEditor) => {
          updateUI()
          return false
        },
        COMMAND_PRIORITY_LOW
      ),
      */
      editor.registerCommand(
        FORMAT_BLOCK_PARAGRAPH_COMMAND,
        () => {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            // const { color, fontSize } = typographyRef.current
            $setBlocksType(selection, $createParagraphNode)
            return true
          }
          return false
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        FORMAT_BLOCK_HEADING_COMMAND,
        (level) => {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            const color = $getSelectionStyleValueForProperty(
              selection,
              'color',
              DEFAULT_TEXT_TYPOGRAPY.color
            )
            $patchStyleText(selection, { color, 'font-size': null })
            // const node = $createHeadingNode(level)
            // node.setStyle(`color: ${typographyRef.current.color}, font-size: 36px`)
            $setBlocksType(selection, () => $createHeadingNode(level))
            return true
          }
          return false
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        FORMAT_BLOCK_LIST_COMMAND,
        (variant) => {
          $insertList(variant)
          return true
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        FORMAT_BLOCK_QUOTE_COMMAND,
        () => {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            $setBlocksType(selection, $createQuoteNode)
            return true
          }
          return false
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        FORMAT_COLOR_COMMAND,
        (color) => {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            $patchStyleText(selection, { color })
            return true
          }
          return false
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        FORMAT_FONT_SIZE_COMMAND,
        (fontSize) => {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            $patchStyleText(selection, { 'font-size': fontSize })
            return true
          }
          return false
        },
        COMMAND_PRIORITY_LOW
      )
      /*
      editor.registerNodeTransform(ParagraphNode, (node: ParagraphNode) => {
        if (node instanceof StyledParagraphNode) return
        const { color, fontSize } = typographyRef.current
        const styledNode = $createStyledParagraphNode(color, fontSize)
        node.replace(styledNode)
      })
      */
    )
  }, [editor, updateUI])

  return (
    <InputGroup className="w-10 me-2">
      <DropdownButton
        as={ButtonGroup}
        title={<Icon type={textTypography.blockType} style={{ display: 'inline-block' }} />}
        id="insert-dropdown"
        variant="outline-secondary"
      >
        <Dropdown.Item
          eventKey="p"
          onClick={() => editor.dispatchCommand(FORMAT_BLOCK_PARAGRAPH_COMMAND, undefined)}
        >
          <Icon type="paragraph" />
        </Dropdown.Item>
        {BLOCK_HEADING_LEVELS.map((level) => (
          <Dropdown.Item
            key={level}
            eventKey={level}
            onClick={() => {
              editor.dispatchCommand(FORMAT_BLOCK_HEADING_COMMAND, level)
            }}
          >
            <Icon type={level} />
          </Dropdown.Item>
        ))}
        <Dropdown.Item
          eventKey="p"
          onClick={() => editor.dispatchCommand(FORMAT_BLOCK_QUOTE_COMMAND, undefined)}
        >
          <Icon type="quote" />
        </Dropdown.Item>
        <Dropdown.Item
          eventKey="number"
          onClick={() => editor.dispatchCommand(FORMAT_BLOCK_LIST_COMMAND, 'number')}
        >
          <Icon type="number" />
        </Dropdown.Item>
        <Dropdown.Item
          eventKey="bullet"
          onClick={() => editor.dispatchCommand(FORMAT_BLOCK_LIST_COMMAND, 'bullet')}
        >
          <Icon type="bullet" />
        </Dropdown.Item>
      </DropdownButton>
      <Form.Control
        type="color"
        value={textTypography.color}
        style={{ width: 40 }}
        onChange={(e) => {
          editor.dispatchCommand(FORMAT_COLOR_COMMAND, e.target.value)
          setTextTypography({ ...textTypography, color: e.target.value })
        }}
      />
      <Form.Control
        type="text"
        placeholder="Enter font size"
        value={textTypography.fontSize.slice(0, -2)}
        readOnly
        style={{ width: 50 }}
      />
      <Dropdown>
        <Dropdown.Toggle split variant="outline-secondary" />
        <Dropdown.Menu>
          {FONT_SIZES.map((fontSize) => (
            <Dropdown.Item
              key={fontSize}
              onClick={() => {
                editor.dispatchCommand(FORMAT_FONT_SIZE_COMMAND, fontSize)
              }}
            >
              {fontSize.slice(0, -2)}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </InputGroup>
  )
}
